import { useState } from 'react';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------
/** @type {any} */
const IconifyAny = Iconify;

const ISSUE_TYPES = [
  { value: 'DATA_INCORRECT', label: 'Data is incorrect' },
  { value: 'INFORMATION_NOT_SUPPORTED', label: 'Information was not supported by the data' },
  { value: 'DID_NOT_ANSWER_QUESTION', label: 'Did not answer my question' },
  { value: 'ANSWER_INCOMPLETE', label: 'Answer was incomplete' },
  { value: 'MISUNDERSTOOD_REQUEST', label: 'Misunderstood my request' },
  { value: 'RESPONSE_OFF_TOPIC', label: 'Response was off topic' },
  { value: 'TECHNICAL_OR_DISPLAY_ISSUE', label: 'Technical or display issue' },
  { value: 'OTHER', label: 'Other' },
];

const DISCLAIMER = 'Submitting this report will share this response with the oneVIEW team to help improve future responses.';

export function ChatMessageFeedback({ value, onSubmit }) {
  const [dialogRating, setDialogRating] = useState(null);
  const [issueType, setIssueType] = useState('');
  const [comment, setComment] = useState('');

  const handleOpen = (rating) => {
    if (value === rating) {
      onSubmit?.(null);
      return;
    }
    setIssueType('');
    setComment('');
    setDialogRating(rating);
  };

  const handleClose = () => setDialogRating(null);

  const handleSubmit = () => {
    onSubmit?.({
      rating: dialogRating,
      ...(dialogRating === 'negative' && { issueType }),
      comment: comment.trim(),
    });
    setDialogRating(null);
  };

  return (
    <>
      <Stack direction="row" spacing={0.5}>
        <Tooltip title={value === 'positive' ? 'Remove feedback' : 'Give positive feedback'}>
          <IconButton size="small" aria-label="Positive feedback" onClick={() => handleOpen('positive')} sx={{ color: value === 'positive' ? 'primary.main' : 'text.secondary' }}>
            <IconifyAny icon={value === 'positive' ? 'solar:like-bold' : 'solar:like-linear'} width={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title={value === 'negative' ? 'Remove feedback' : 'Give negative feedback'}>
          <IconButton size="small" aria-label="Negative feedback" onClick={() => handleOpen('negative')} sx={{ color: value === 'negative' ? 'error.main' : 'text.secondary' }}>
            <IconifyAny icon={value === 'negative' ? 'solar:dislike-bold' : 'solar:dislike-linear'} width={18} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog open={!!dialogRating} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogRating === 'negative' ? 'Give negative feedback' : 'Give positive feedback'}</DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {dialogRating === 'negative' && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">What type of issue do you wish to report? (optional)</Typography>
                <TextField select value={issueType} onChange={(e) => setIssueType(e.target.value)} SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="">
                    <Typography component="span" color="text.disabled">Select...</Typography>
                  </MenuItem>
                  {ISSUE_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}

            <Stack spacing={1}>
              <Typography variant="body2">Please provide details: (optional)</Typography>
              <TextField
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  dialogRating === 'negative'
                    ? 'What was not valuable about this response?'
                    : 'What was valuable about this response?'
                }
              />
            </Stack>

            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              {DISCLAIMER}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="inherit" onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
